import { Button } from '@getethos/ethos-design-system-v2';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Roles } from '../../../common/constants/roles';
import { UserOrganization } from '../../../common/constants/userOrganization';
import { FetchService } from '../../../common/fetch.service';
import { useDebounce } from '../../../common/hooks/useDebounce';
import {
  QACaseListResponse,
  QACaseResponseDto,
  QACaseStatus,
  QACaseType,
  QACaseUser,
} from '../../../common/interfaces/IQaCase';
import { ProductFactory } from '../../../common/productConfig/productFactory';
import { SnackBarType } from '../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../layout/SnackbarContext';
import { AuthContext } from '../../login/AuthWrapper';
import { IUnderwriter } from '../../peer-review/types';
import {
  assignNextQACaseEndPoint,
  getQACasesEndPoint,
} from '../../PPWB/api.endPoints';
import styles from './qaListPage.module.scss';

// Extend dayjs with duration plugin
dayjs.extend(duration);

export const QAListPage = () => {
  const auth = useContext(AuthContext);
  const isAdmin = auth.roles.some(role =>
    [Roles.ADMIN, Roles.WORKBENCH_ADMIN].includes(role),
  );
  const { openSnacks, setSnacks } = useContext(SnackbarContext);

  const { data: underwriters = [] } = useQuery<IUnderwriter[]>({
    queryKey: ['underwriters'],
    queryFn: () => FetchService.getInstance().get('/users/underwriters'),
  });

  const adminAndStrategryReviewerUnderwriters = useMemo(() => {
    return underwriters.filter(
      uw =>
        uw.roles.includes(Roles.ADMIN) ||
        uw.roles.includes(Roles.UW_STRATEGY_REVIEWER) ||
        // v2 roles
        uw.roles.includes(Roles.WORKBENCH_ADMIN) ||
        uw.roles.includes(Roles.WORKBENCH_STRATEGY_REVIEWER),
    );
  }, [underwriters]);

  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  const [policyIdSearch, setPolicyIdSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<QACaseStatus>();
  const [productFilter, setProductFilter] = useState<string>('');

  // Product filter options based on user organization
  const productFilterOptions = useMemo(() => {
    return ProductFactory.getProductsByOrganization(
      auth.userOrganization as UserOrganization,
    ).map(product => ({
      value: product.getProduct(),
      label: product.getProductDisplayText(),
    }));
  }, [auth.userOrganization]);

  // if user is admin default is empty
  // if not admin, default is current user
  const [assigneeFilter, setAssigneeFilter] = useState<string | undefined>(
    undefined,
  ); // Default to current user
  useEffect(() => {
    if (isAdmin) {
      setAssigneeFilter('');
    } else {
      setAssigneeFilter(auth.id?.toString() ?? '');
    }
  }, [isAdmin, auth.id]);

  // Debounce the search input to prevent excessive API calls
  const debouncedPolicyIdSearch = useDebounce(policyIdSearch, 500);

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    enabled: assigneeFilter !== undefined,
    queryKey: [
      'qaCaseList',
      page,
      debouncedPolicyIdSearch,
      statusFilter,
      assigneeFilter,
      productFilter,
    ],
    queryFn: () =>
      FetchService.getInstance().get<QACaseListResponse>(
        getQACasesEndPoint({
          page: page + 1,
          limit: PAGE_SIZE,
          status: statusFilter,
          assignedTo: assigneeFilter ? Number(assigneeFilter) : undefined,
          product: productFilter || undefined,
          policyId: debouncedPolicyIdSearch
            ? Number(debouncedPolicyIdSearch)
            : undefined,
        }),
      ),
    refetchInterval: 5 * 60 * 1000,
  });

  const assignNextMutation = useMutation({
    mutationFn: () =>
      FetchService.getInstance().post<QACaseResponseDto>(
        assignNextQACaseEndPoint(),
        {},
      ),
    onSuccess: assignedCase => {
      // Navigate to the assigned QA case
      window.open(`/workbench/ppwb/policy/${assignedCase.policyId}`, '_blank');
      // Invalidate and refetch the QA case list to show the updated assignment
      queryClient.invalidateQueries({ queryKey: ['qaCaseList'] });
    },
    onError: (mutationError: any) => {
      console.error('Error assigning next QA case:', mutationError);
      // Handle specific error cases
      if (mutationError?.status === 404) {
        setSnacks([
          ...openSnacks,
          {
            message: 'No unassigned QA cases available',
            type: SnackBarType.ALERT,
            id: uuidv4(),
          },
        ]);
      } else {
        setSnacks([
          ...openSnacks,
          {
            message: `Error assigning QA case: ${mutationError?.message || 'Unknown error'}`,
            type: SnackBarType.ALERT,
            id: uuidv4(),
          },
        ]);
      }
    },
  });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value as QACaseStatus);
    setPage(0);
  };

  const handleAssigneeFilterChange = (event: SelectChangeEvent<string>) => {
    setAssigneeFilter(event.target.value);
    setPage(0);
  };

  const handleProductFilterChange = (event: SelectChangeEvent<string>) => {
    setProductFilter(event.target.value);
    setPage(0);
  };

  const handleAssignNext = () => {
    assignNextMutation.mutate();
  };

  const getStatusDisplayText = (status: QACaseStatus) => {
    switch (status) {
      case QACaseStatus.IN_QA:
        return 'In QA';
      case QACaseStatus.COMPLETED_QA:
        return 'Completed';
      default:
        return status;
    }
  };

  const getAssigneeName = (assignedTo: QACaseUser | null) => {
    if (!assignedTo) return 'Unassigned';
    return assignedTo.name;
  };

  if (error) {
    return <Typography color="error">Error loading QA list</Typography>;
  }

  return (
    <Box className={styles.qaListPage}>
      <Typography variant="h1">QA List</Typography>

      {/* Filters */}
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Policy ID Search"
            variant="outlined"
            size="small"
            value={policyIdSearch}
            onChange={e => setPolicyIdSearch(e.target.value)}
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value={QACaseStatus.IN_QA}>In QA</MenuItem>
              <MenuItem value={QACaseStatus.COMPLETED_QA}>Completed</MenuItem>
              <MenuItem value="">All</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Product</InputLabel>
            <Select
              value={productFilter}
              label="Product"
              onChange={handleProductFilterChange}
            >
              {productFilterOptions.map(product => (
                <MenuItem key={product.value} value={product.value}>
                  {product.label}
                </MenuItem>
              ))}
              <MenuItem value="">All</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={assigneeFilter?.toString() || ''}
              label="Assignee"
              onChange={handleAssigneeFilterChange}
            >
              {adminAndStrategryReviewerUnderwriters.map(underwriter => (
                <MenuItem
                  key={underwriter.id}
                  value={underwriter.id.toString()}
                >
                  {underwriter.displayName}
                </MenuItem>
              ))}
              <MenuItem value="">All</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <span>
          <Button
            buttonTitle={
              assignNextMutation.isLoading ? 'Assigning...' : 'Assign QA Case'
            }
            variant="infoOutline"
            onClick={handleAssignNext}
            disabled={assignNextMutation.isLoading}
          />
        </span>
      </Box>

      {/* Table */}
      <TableContainer className={styles.customTable}>
        <Table>
          <TableHead>
            <TableRow className={styles.tableHeader}>
              <TableCell className={styles.headerCell}>Policy ID</TableCell>
              <TableCell className={styles.headerCell}>Case Type</TableCell>
              <TableCell className={styles.headerCell}>Product</TableCell>
              <TableCell className={styles.headerCell}>Status</TableCell>
              <TableCell className={styles.headerCell}>Assignee</TableCell>
              <TableCell className={styles.headerCell}>SLA</TableCell>
              <TableCell className={styles.headerCell}>Created At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={styles.tableBody}>
            {isLoading
              ? // Loading skeleton rows
                Array.from({ length: PAGE_SIZE }).map((__, rowIndex) => (
                  <TableRow
                    key={`skeleton-${rowIndex}`}
                    className={styles.tableRow}
                  >
                    {Array.from({ length: 7 }).map((_, cellIndex) => (
                      <TableCell
                        key={`skeleton-${rowIndex}-${cellIndex}`}
                        className={styles.bodyCell}
                      >
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : data?.data.map((row: QACaseResponseDto) => (
                  <TableRow key={row.id} className={styles.tableRow}>
                    <TableCell className={styles.bodyCell}>
                      <MuiLink
                        onClick={() =>
                          window.open(
                            `/workbench/ppwb/policy/${row.policyId}`,
                            '_blank',
                          )
                        }
                      >
                        {row.policyId}
                      </MuiLink>
                    </TableCell>
                    <TableCell className={styles.bodyCell}>
                      {row.caseType === QACaseType.ENGINE_RULES_REVIEW && (
                        <Typography>Pre Issue</Typography>
                      )}
                      {row.caseType === QACaseType.AUTO_PIA_RULES_REVIEW && (
                        <Typography>Post Issue</Typography>
                      )}
                    </TableCell>
                    <TableCell className={styles.bodyCell}>
                      {row.product.displayText}
                    </TableCell>
                    <TableCell className={styles.bodyCell}>
                      <Chip
                        label={getStatusDisplayText(row.status)}
                        className={`${styles.statusChip} ${
                          row.status === QACaseStatus.IN_QA
                            ? styles.inQA
                            : styles.completed
                        }`}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: '4px',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell className={styles.bodyCell}>
                      {getAssigneeName(row.assignedTo)}
                    </TableCell>
                    <TableCell className={styles.bodyCell}>
                      {(() => {
                        if (!row.sla) {
                          return (
                            <Chip
                              label="-"
                              color="default"
                              size="small"
                              variant="outlined"
                              sx={{
                                borderRadius: '4px',
                                fontWeight: 500,
                              }}
                            />
                          );
                        }

                        const now = dayjs();
                        const slaDate = dayjs(row.sla);

                        // Check if the SLA date is valid
                        if (!slaDate.isValid()) {
                          return (
                            <Chip
                              label="Invalid SLA"
                              color="warning"
                              size="small"
                              variant="outlined"
                              sx={{
                                borderRadius: '4px',
                                fontWeight: 500,
                              }}
                            />
                          );
                        }

                        const diffMs = slaDate.diff(now);
                        const isOverdue = diffMs < 0;
                        const absDiff = Math.abs(diffMs);
                        const absDuration = dayjs.duration(absDiff);

                        const days = Math.floor(absDuration.asDays());
                        const hours = absDuration.hours();

                        let label = '';
                        if (days > 0) {
                          label += `${days}d `;
                        }
                        if (hours > 0 || days > 0) {
                          label += `${hours}h `;
                        }

                        return (
                          <Chip
                            label={label}
                            color={isOverdue ? 'error' : 'success'}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: '4px',
                              fontWeight: 500,
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell className={styles.bodyCell}>
                      {dayjs(row.createdAt).format('MMM D, YYYY, hh:mm A')}{' '}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={data?.meta.total || 0}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
      />
    </Box>
  );
};
